import { useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, onSnapshot, query, where, doc, setDoc, deleteDoc, addDoc, getDocs } from "firebase/firestore";
import { btnStyle, btnSecondaryStyle, inputStyle, cardStyle, colors } from "./lib/theme";
import { formatDayTime, formatDate, occurrencesWithinDays, dateKey, todayKey, isToday, ROLLING_WINDOW_DAYS } from "./lib/weekUtils";

var warningColor = "#B8860B";
var warningBg = "rgba(184,134,11,0.08)";
var warningBorder = "rgba(184,134,11,0.3)";

export default function PollPage({ user, onOrganise }) {
  var [templates, setTemplates] = useState([]);
  var [venues, setVenues] = useState([]);
  var [votes, setVotes] = useState([]);
  var [dependents, setDependents] = useState([]);
  var [newDependentName, setNewDependentName] = useState("");
  var [allMembers, setAllMembers] = useState([]);

  useEffect(function () {
    var unsubTpl = onSnapshot(collection(db, "sessionTemplates"), function (snap) {
      setTemplates(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }).filter(function (t) { return t.active; }));
    });
    var unsubVenues = onSnapshot(collection(db, "venues"), function (snap) {
      setVenues(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
    var unsubVotes = onSnapshot(query(collection(db, "pollVotes"), where("occurrenceDate", ">=", todayKey())), function (snap) {
      setVotes(snap.docs.map(function (d) { return d.data(); }));
    });
    var unsubDeps = onSnapshot(query(collection(db, "dependents"), where("ownerId", "==", user.uid)), function (snap) {
      setDependents(snap.docs.map(function (d) { return { id: d.id, ...d.data() }; }));
    });
    getDocs(collection(db, "users")).then(function (snap) {
      setAllMembers(snap.docs.map(function (d) { return { uid: d.id, displayName: d.data().displayName || "" }; }));
    });
    return function () { unsubTpl(); unsubVenues(); unsubVotes(); unsubDeps(); };
  }, [user.uid]);

  var venuesById = {};
  venues.forEach(function (v) { venuesById[v.id] = v; });

  var voterOptions = [{ uid: user.uid, displayName: user.displayName }].concat(
    dependents.map(function (d) { return { uid: d.linkedUid || d.id, displayName: d.displayName }; })
  );

  function votesFor(templateId, occKey) {
    return votes.filter(function (v) { return v.templateId === templateId && v.occurrenceDate === occKey; });
  }

  function hasVoted(templateId, occKey, voterUid) {
    return votes.some(function (v) { return v.templateId === templateId && v.occurrenceDate === occKey && v.uid === voterUid; });
  }

  function toggleVote(templateId, occKey, voterUid, voterName, blocked) {
    var voteId = occKey + "_" + templateId + "_" + voterUid;
    if (hasVoted(templateId, occKey, voterUid)) {
      deleteDoc(doc(db, "pollVotes", voteId));
    } else {
      if (blocked) return;
      setDoc(doc(db, "pollVotes", voteId), {
        occurrenceDate: occKey, templateId: templateId, uid: voterUid, votedBy: user.uid,
        displayName: voterName, votedAt: new Date().toISOString(),
      });
    }
  }

  function addDependent(e) {
    e.preventDefault();
    var name = newDependentName.trim();
    if (!name) return;
    var lower = name.toLowerCase();

    var matchedMember = allMembers.find(function (m) { return (m.displayName || "").trim().toLowerCase() === lower; });

    if (matchedMember) {
      var alreadyLinked = dependents.some(function (d) { return d.linkedUid === matchedMember.uid; });
      if (alreadyLinked) {
        window.alert("You can already vote for " + matchedMember.displayName + " — they're in your household list below.");
        return;
      }
      var wantsLink = window.confirm(
        "\"" + name + "\" already has their own account. Vote using their existing account instead of creating a " +
        "separate profile?\n\n(Recommended — keeps their votes and results all in one place.)"
      );
      if (wantsLink) {
        setDoc(doc(db, "dependents", user.uid + "_" + matchedMember.uid), {
          ownerId: user.uid,
          displayName: matchedMember.displayName,
          linkedUid: matchedMember.uid,
          createdAt: new Date().toISOString(),
        });
        setNewDependentName("");
        return;
      }
      var addSeparately = window.confirm(
        "Add \"" + name + "\" as a completely separate person instead? Only do this if they're actually a " +
        "different individual who happens to share a name."
      );
      if (!addSeparately) return;
    }

    var matchesOwnDependent = dependents.some(function (d) { return d.displayName.trim().toLowerCase() === lower; });
    if (matchesOwnDependent) {
      window.alert("You already have someone named \"" + name + "\" in your household.");
      return;
    }

    addDoc(collection(db, "dependents"), {
      ownerId: user.uid,
      displayName: name,
      createdAt: new Date().toISOString(),
    });
    setNewDependentName("");
  }

  function removeDependent(dep) {
    if (window.confirm("Remove " + dep.displayName + " from your household? Their past votes/results aren't affected.")) {
      deleteDoc(doc(db, "dependents", dep.id));
    }
  }

  var instances = templates.flatMap(function (t) {
    var excluded = t.excludedDates || [];
    return occurrencesWithinDays(t.dayOfWeek, t.time, ROLLING_WINDOW_DAYS)
      .filter(function (date) { return excluded.indexOf(dateKey(date)) === -1; })
      .map(function (date) { return { template: t, date: date, occKey: dateKey(date) }; });
  }).sort(function (a, b) { return a.date - b.date; });

  return (
    <div>
      <h2 style={{ fontSize: 18, marginBottom: 4 }}>Sessions — Next {ROLLING_WINDOW_DAYS} Days</h2>
      <p style={{ fontSize: 13, color: colors.textDim, marginTop: 0, marginBottom: 16 }}>
        Vote for one or more sessions you plan to join.
      </p>

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, fontSize: 14 }}>Your Household</h3>
        <p style={{ fontSize: 12, color: colors.textDim, marginTop: 0 }}>
          Add anyone without their own login — a child, a partner — so you can vote and sign them in without them needing an account.
        </p>
        {dependents.map(function (d) {
          return (
            <div key={d.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid " + colors.border, fontSize: 14 }}>
              <span>
                {d.displayName}
                {d.linkedUid && <span style={{ fontSize: 11, color: colors.textFaint, marginLeft: 6 }}>(their own account)</span>}
              </span>
              <button onClick={function () { removeDependent(d); }} style={{ background: "none", border: "none", color: colors.danger, cursor: "pointer", fontSize: 12 }}>Remove</button>
            </div>
          );
        })}
        <form onSubmit={addDependent} style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <input style={inputStyle} value={newDependentName} onChange={function (e) { setNewDependentName(e.target.value); }} placeholder="e.g. Jack (son)" />
          <button type="submit" style={{ ...btnSecondaryStyle, width: "auto", padding: "0 16px" }}>Add</button>
        </form>
      </div>

      {instances.length === 0 && (
        <div style={cardStyle}><p style={{ margin: 0, fontSize: 14, color: colors.textDim }}>No sessions have been set up yet — ask an admin to add one.</p></div>
      )}

      {instances.map(function (inst) {
        var t = inst.template;
        var venue = venuesById[t.venueId];
        var voterList = votesFor(t.id, inst.occKey);
        var today = isToday(inst.date);
        var capacity = venue ? (venue.courtCount || 1) * 4 : null;
        var maxPlayers = capacity ? capacity * 2 : null;
        var pct = capacity ? voterList.length / capacity : 0;
        var isFull = !!capacity && pct >= 2.0;
        var isGettingFull = !!capacity && pct >= 1.5 && pct < 2.0;
        var anyOfMineVoted = voterOptions.some(function (opt) { return hasVoted(t.id, inst.occKey, opt.uid); });

        return (
          <div key={inst.occKey + "_" + t.id} style={{ ...cardStyle, marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{formatDayTime(t.dayOfWeek, t.time, t.endTime)}</div>
                <div style={{ fontSize: 13, color: colors.textDim }}>{t.venueName}</div>
                <div style={{ fontSize: 12, color: colors.textFaint, marginTop: 2 }}>
                  {formatDate(inst.date)}{today ? " · today" : ""}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: colors.accent }}>{voterList.length}</div>
                <div style={{ fontSize: 11, color: colors.textDim }}>{"voted" + (maxPlayers ? " / " + maxPlayers : "")}</div>
              </div>
            </div>

            {isFull && (
              <div style={{ background: colors.dangerBg, border: "1px solid " + colors.dangerBorder, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: colors.danger, marginTop: 10 }}>
                This session is full — voting is closed. You can still remove an existing vote.
              </div>
            )}
            {isGettingFull && !isFull && (
              <div style={{ background: warningBg, border: "1px solid " + warningBorder, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: warningColor, marginTop: 10 }}>
                Getting full — {voterList.length} of {capacity} spots taken.
              </div>
            )}

            {voterList.length > 0 && (
              <div style={{ fontSize: 12, color: colors.textFaint, marginTop: 8 }}>
                {voterList.map(function (v) { return v.displayName; }).join(", ")}
              </div>
            )}

            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              {voterOptions.map(function (opt) {
                var voted = hasVoted(t.id, inst.occKey, opt.uid);
                var blocked = isFull && !voted;
                return (
                  <button
                    key={opt.uid}
                    onClick={function () { toggleVote(t.id, inst.occKey, opt.uid, opt.displayName, blocked); }}
                    disabled={blocked}
                    style={voted
                      ? { ...btnSecondaryStyle, borderColor: colors.accent2, color: colors.accent2 }
                      : blocked ? { ...btnSecondaryStyle, opacity: 0.5, cursor: "not-allowed" } : btnStyle}
                  >
                    {voted ? "✓ " + opt.displayName + " — tap to remove" : blocked ? opt.displayName + " — session full" : "Vote for " + opt.displayName}
                  </button>
                );
              })}
            </div>

            {today && anyOfMineVoted && onOrganise && (
              <button
                onClick={function () { onOrganise(t, inst.date); }}
                style={{ ...btnSecondaryStyle, marginTop: 10 }}
              >
                Run this session
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
